import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterItem, HotelFilterResult, HotelFilterService } from '../../../services/hotel-filter.service';
import { SearchFilterItem } from '../../../services/search.service';

export type SortOption = 'Average' | 'PriceAscending' | 'PriceDescending';

interface FilterGroup {
  title: string;
  key: keyof Omit<SearchFilterItem, 'filterPriceRanges' | 'filterAvailableTypeCode' | 'filterHotelName'>;
  items: FilterItem[];
  codeType: 'string' | 'number';
  expanded: boolean;
  searchTerm?: string;
}

@Component({
  selector: 'app-hotel-filter-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hotel-filter-sidebar.component.html',
  styleUrl: './hotel-filter-sidebar.component.css'
})
export class HotelFilterSidebarComponent implements OnChanges {
  @Input() filterResult: HotelFilterResult | null = null;
  @Input() loading = false;
  @Input() isMobileOpen = false;
  @Input() totalCount = 0;
  @Input() isDatelessSearch = false;

  @Output() filtersApplied = new EventEmitter<{ filters: SearchFilterItem; orderBy: SortOption }>();
  @Output() mobileClose = new EventEmitter<void>();

  // Mevcut filtre durumu
  minPrice: number | null = null;
  maxPrice: number | null = null;
  hotelName = '';
  availableTypeCode: number | null = null;

  // Sıralama — Gordios OrderBy parametresi
  orderBy: SortOption = 'Average';
  readonly sortOptions: Array<{ value: SortOption; label: string }> = [
    { value: 'Average', label: 'Önerilen' },
    { value: 'PriceAscending', label: 'Fiyata Göre Artan' },
    { value: 'PriceDescending', label: 'Fiyata Göre Azalan' },
  ];

  // Dinamik gruplar (checkbox listeleri)
  groups: FilterGroup[] = [];

  // Seçili state (grup.key -> Set<code>)
  private selected = new Map<string, Set<string | number>>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterResult']) {
      this.buildGroups();
    }
  }

  private buildGroups(): void {
    const r = this.filterResult;
    if (!r) { this.groups = []; return; }

    const g = (
      title: string,
      key: FilterGroup['key'],
      items: FilterItem[],
      codeType: FilterGroup['codeType']
    ): FilterGroup => ({ title, key, items: items ?? [], codeType, expanded: false });

    const groups: FilterGroup[] = [
      g('Lokasyon', 'filterLocationIds',
        HotelFilterService.flatten<FilterItem>(r.locations), 'number'),
      g('Tema', 'filterHotelThemeCodes',
        HotelFilterService.flatten<FilterItem>(r.hotelThemes), 'string'),
      g('Konsept (Yemek)', 'filterMealTypeCodes',
        HotelFilterService.flatten<FilterItem>(r.mealTypes), 'string'),
      g('Otel Tipi', 'filterHotelTypeCodes',
        HotelFilterService.flatten<FilterItem>(r.hotelTypes), 'string'),
      g('Kategori', 'filterCategoryTypeCodes',
        HotelFilterService.flatten<FilterItem>(r.categoryTypes), 'number'),
      g('Otel Olanakları', 'filterHotelFacilityCodes',
        HotelFilterService.flatten<FilterItem>(r.hotelFacilities), 'string'),
      g('Oda Olanakları', 'filterHotelRoomFacilityCodes',
        HotelFilterService.flatten<FilterItem>(r.hotelRoomFacilities), 'string'),
      g('Yiyecek & İçecek', 'filterFoodAndDrinkCodes',
        HotelFilterService.flatten<FilterItem>(r.foodAndDrinks), 'string'),
      g('Yakın Mesafe', 'filterNearDistanceCodes',
        HotelFilterService.flatten<FilterItem>(r.nearDistances), 'string'),
      g('Hijyen Önlemleri', 'filterHygienicalMeasureCodes',
        HotelFilterService.flatten<FilterItem>(r.hygienicalMeasures), 'string'),
    ];

    console.log('[Sidebar] raw groups counts:',
      groups.map(g => `${g.title}:${g.items.length}`).join(', '));

    // Boş grupları eleme — sadece item'ı olan grupları göster
    this.groups = groups.filter(x => x.items.length > 0);

    console.log('[Sidebar] görünür grup sayısı:', this.groups.length,
      'titles:', this.groups.map(g => g.title).join(', '));

    // İlk 3 grubu default açık yap (daha iyi UX)
    this.groups.slice(0, 3).forEach(g => g.expanded = true);
  }

  toggleGroup(group: FilterGroup): void {
    group.expanded = !group.expanded;
  }

  isChecked(group: FilterGroup, item: FilterItem): boolean {
    return this.selected.get(group.key)?.has(item.code) ?? false;
  }

  toggleItem(group: FilterGroup, item: FilterItem, checked: boolean): void {
    let set = this.selected.get(group.key);
    if (!set) { set = new Set(); this.selected.set(group.key, set); }
    if (checked) set.add(item.code);
    else set.delete(item.code);
  }

  filteredItems(group: FilterGroup): FilterItem[] {
    const term = (group.searchTerm ?? '').trim().toLocaleLowerCase('tr');
    if (!term) return group.items;
    return group.items.filter(i => i.name?.toLocaleLowerCase('tr').includes(term));
  }

  selectedCount(group: FilterGroup): number {
    return this.selected.get(group.key)?.size ?? 0;
  }

  apply(): void {
    const payload: SearchFilterItem = {};

    if (this.minPrice != null || this.maxPrice != null) {
      payload.filterPriceRanges = {
        minPrice: this.minPrice ?? null,
        maxPrice: this.maxPrice ?? null,
      };
    }

    const name = this.hotelName?.trim();
    if (name) payload.filterHotelName = name;

    if (this.availableTypeCode !== null) payload.filterAvailableTypeCode = this.availableTypeCode;

    for (const group of this.groups) {
      const set = this.selected.get(group.key);
      if (!set || set.size === 0) continue;
      const arr = Array.from(set);
      if (group.codeType === 'number') {
        (payload as any)[group.key] = arr.map(v => Number(v));
      } else {
        (payload as any)[group.key] = arr.map(v => String(v));
      }
    }

    this.filtersApplied.emit({ filters: payload, orderBy: this.orderBy });
    this.mobileClose.emit();
  }

  clearAll(): void {
    this.minPrice = null;
    this.maxPrice = null;
    this.hotelName = '';
    this.availableTypeCode = null;
    this.selected.clear();
    this.groups.forEach(g => g.searchTerm = '');
    this.orderBy = 'Average';
    this.filtersApplied.emit({ filters: {}, orderBy: 'Average' });
    this.mobileClose.emit();
  }

  /**
   * Sıralama değişince anında uygula — filtre bekletmeden.
   */
  onSortChange(value: SortOption): void {
    this.orderBy = value;
    this.apply();
  }

  get totalSelectedCount(): number {
    let n = 0;
    this.selected.forEach(s => n += s.size);
    if (this.minPrice != null || this.maxPrice != null) n++;
    if (this.hotelName?.trim()) n++;
    if (this.availableTypeCode !== null) n++;
    return n;
  }

  closeMobile(): void {
    this.mobileClose.emit();
  }

  trackByCode(_: number, item: FilterItem): string | number {
    return item.code;
  }

  trackByKey(_: number, g: FilterGroup): string {
    return g.key;
  }
}
